import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ResponseArtifactDTO } from '@dreamai/types';
import api from '../../../api';
import CredentialSheet from './CredentialSheet';
import { SiteGlyph, displayHost } from './CredentialSheetSections';
import SecureInputStage from './SecureInputStage';
import { LockIcon } from './transactional-icons';
import { PAYMENT_PRIVATE_ONLY_MESSAGE } from './purchaseApprovalArtifact';
import './secure-input-sheet.scss';
import './payment-challenge-modal.scss';

/**
 * Owner-only live bank challenge ("confirm with your bank"). Pay has been
 * pressed and the bank showed its own page — a code, "approve in your app", a
 * bank password. The owner opens a private, time-boxed live view of the
 * checkout, finishes the bank's step there and presses Done; the executor then
 * reads the result. Nothing typed into the live view passes through Dream.
 *
 * Deliberately separate from SecureInputRequestModal (the CVV / bank-code
 * sheets): there is no code field, no share-token mode, and the live-view URL
 * is held in component state only — never persisted, logged or rendered as
 * text. Talks to the JWT-only `/secure-payments/challenges/:id` routes.
 */

interface ChallengeStatus {
  secureRequestId: string;
  state: string;
  reasonCode: string | null;
  expiresAt: string;
  opened: boolean;
}

interface OpenResponse {
  liveViewUrl: string;
  expiresAt: string;
}

interface PaymentChallengeModalProps {
  show: boolean;
  artifact: ResponseArtifactDTO;
  secureRequestId: string;
  /**
   * The private thread the challenge was delivered to
   * (`artifact.parentThreadId`). The server only accepts open/done from that
   * thread, so it travels with every action.
   */
  threadId: string;
  onClose: () => void;
  onOptimisticAction?: (
    artifact: ResponseArtifactDTO,
    action: 'submit' | 'dismiss',
  ) => void;
  readOnly?: boolean;
  /** Public share page: the challenge is never shown there. */
  shareToken?: string;
}

type Phase =
  | 'loading'
  | 'ready'
  | 'opening'
  | 'opened'
  | 'checking'
  | 'paid'
  | 'expired'
  | 'closed';

const GENERIC_ERROR = "Couldn't reach Dream. Please try again.";
const NO_LONGER_AVAILABLE =
  'This bank verification is no longer available. Check your Dream chat for the latest on this payment.';
const OPENED_ELSEWHERE =
  "You've already opened the bank page in another window. Finish it there, then press Done here.";
const BANK_PAGE_UNAVAILABLE =
  "The bank page can't be shown right now. Dream will check the payment and let you know what happened.";

const WAITING_STATES: ReadonlySet<string> = new Set(['awaiting_three_ds']);
const CHECKING_STATES: ReadonlySet<string> = new Set([
  'submitted',
  'verifying',
  'filling',
]);

/** Plain-English close-out for the sanitized reason codes the sheet may see. */
export const challengeOutcomeMessage = (
  state: string,
  reasonCode: string | null,
): string => {
  if (state === 'paid') return 'Payment confirmed. Dream will carry on.';
  switch (reasonCode) {
    case 'three_ds_failed':
      return "Your bank didn't confirm the payment, so nothing was charged. Dream will ask how you'd like to continue.";
    case 'payment_declined':
      return 'The payment was declined, so nothing was charged. Dream will ask how you’d like to continue.';
    case 'three_ds_expired':
    case 'checkout_expired':
    case 'approval_expired':
      return 'This bank verification ran out of time. Dream will check whether anything was paid before doing anything else.';
    case 'unknown_outcome':
      return "Dream couldn't confirm how the payment ended and will check the order before doing anything else. Nothing will be paid twice.";
    case 'cancelled':
      return 'This payment was cancelled.';
    default:
      return 'Dream will check the payment status and let you know what happened.';
  }
};

const statusCode = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined;
  }
  const response = error.response;
  if (typeof response !== 'object' || response === null) return undefined;
  if (!('status' in response) || typeof response.status !== 'number') {
    return undefined;
  }
  return response.status;
};

const formatRemaining = (expiresAt: string | null, now: number): string => {
  if (!expiresAt) return '';
  const remaining = Math.max(0, new Date(expiresAt).getTime() - now);
  const seconds = Math.ceil(remaining / 1000);
  if (seconds >= 60) return `${Math.ceil(seconds / 60)} min`;
  return `${seconds} sec`;
};

const PaymentChallengeModal: React.FC<PaymentChallengeModalProps> = ({
  show,
  artifact,
  secureRequestId,
  threadId,
  onClose,
  onOptimisticAction,
  readOnly = false,
  shareToken,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const basePath = `/secure-payments/challenges/${secureRequestId}`;
  const pollTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [message, setMessage] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [liveViewUrl, setLiveViewUrl] = useState<string | null>(null);
  const [origin, setOrigin] = useState<string>('');
  const [now, setNow] = useState(Date.now());

  const authConfig = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const settle = useCallback(
    (nextPhase: 'paid' | 'expired' | 'closed', text: string | null) => {
      stopPolling();
      setLiveViewUrl(null);
      setPhase(nextPhase);
      setMessage(text);
      if (nextPhase === 'paid') {
        onOptimisticAction?.(artifact, 'submit');
      }
    },
    [artifact, onOptimisticAction, stopPolling],
  );

  /** Applies a sanitized status; returns true once the challenge is settled. */
  const applyStatus = useCallback(
    (status: ChallengeStatus, keepLiveView: boolean): boolean => {
      setExpiresAt(status.expiresAt);
      if (status.state === 'paid') {
        settle('paid', challengeOutcomeMessage('paid', status.reasonCode));
        return true;
      }
      if (status.state === 'expired' || status.state === 'cancelled') {
        settle(
          status.state === 'expired' ? 'expired' : 'closed',
          challengeOutcomeMessage(status.state, status.reasonCode),
        );
        return true;
      }
      if (status.state === 'cannot_continue') {
        settle(
          'closed',
          challengeOutcomeMessage(status.state, status.reasonCode),
        );
        return true;
      }
      if (CHECKING_STATES.has(status.state)) {
        setLiveViewUrl(null);
        setPhase('checking');
        return false;
      }
      if (WAITING_STATES.has(status.state)) {
        if (new Date(status.expiresAt).getTime() <= Date.now()) {
          settle('expired', challengeOutcomeMessage('expired', null));
          return true;
        }
        if (!keepLiveView) {
          setPhase('ready');
          setNotice(status.opened ? OPENED_ELSEWHERE : null);
        }
        return false;
      }
      // Any other state means the executor is somewhere we cannot help with.
      settle('closed', NO_LONGER_AVAILABLE);
      return true;
    },
    [settle],
  );

  const fetchStatus = useCallback(
    async (keepLiveView: boolean): Promise<boolean> => {
      try {
        const response = await api.get<ChallengeStatus>(
          basePath,
          await authConfig(),
        );
        return applyStatus(response.data, keepLiveView);
      } catch (error) {
        const status = statusCode(error);
        if (status === 410) {
          settle('expired', challengeOutcomeMessage('expired', null));
          return true;
        }
        if (status === 403) {
          settle('closed', PAYMENT_PRIVATE_ONLY_MESSAGE);
          return true;
        }
        if (status === 404) {
          settle('closed', NO_LONGER_AVAILABLE);
          return true;
        }
        return false;
      }
    },
    [applyStatus, authConfig, basePath, settle],
  );

  const startPolling = useCallback(() => {
    stopPolling();
    pollTimerRef.current = window.setInterval(() => {
      void fetchStatus(true).then((settled) => {
        if (settled) stopPolling();
      });
    }, 2000);
  }, [fetchStatus, stopPolling]);

  useEffect(() => {
    if (!show) return undefined;
    setMessage(null);
    setNotice(null);
    setLiveViewUrl(null);
    setExpiresAt(null);
    setNow(Date.now());
    setOrigin(
      typeof (artifact as { credentialSite?: unknown }).credentialSite ===
        'string'
        ? (artifact as { credentialSite: string }).credentialSite
        : '',
    );
    if (shareToken) {
      setPhase('closed');
      setMessage(PAYMENT_PRIVATE_ONLY_MESSAGE);
      return undefined;
    }
    setPhase('loading');
    void fetchStatus(false).then((settled) => {
      // The executor may already be checking the result when the sheet opens.
      if (!settled) startPolling();
    });
    return () => {
      stopPolling();
      setLiveViewUrl(null);
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [artifact, fetchStatus, shareToken, show, startPolling, stopPolling]);

  useEffect(() => {
    if (!show || !expiresAt) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt, show]);

  useEffect(() => {
    if (!show || !expiresAt) return undefined;
    if (
      (phase === 'ready' || phase === 'opened' || phase === 'opening') &&
      new Date(expiresAt).getTime() <= now
    ) {
      // Time is up; the server-side timeout closes it out and tells the agent.
      setLiveViewUrl(null);
      setPhase('checking');
      startPolling();
    }
    return undefined;
  }, [expiresAt, now, phase, show, startPolling]);

  const handleOpen = async () => {
    if (phase !== 'ready' || readOnly) return;
    setPhase('opening');
    setMessage(null);
    try {
      const response = await api.post<OpenResponse>(
        `${basePath}/open`,
        { threadId },
        await authConfig(),
      );
      setExpiresAt(response.data.expiresAt);
      setLiveViewUrl(response.data.liveViewUrl);
      setNotice(null);
      setPhase('opened');
    } catch (error) {
      const status = statusCode(error);
      if (status === 409) {
        // Already opened (another window) or the executor has moved on.
        const settled = await fetchStatus(false);
        if (!settled) {
          setNotice(OPENED_ELSEWHERE);
          setPhase('ready');
        }
      } else if (status === 410) {
        settle('expired', challengeOutcomeMessage('expired', null));
      } else if (status === 403) {
        settle('closed', PAYMENT_PRIVATE_ONLY_MESSAGE);
      } else if (status === 404) {
        settle('closed', NO_LONGER_AVAILABLE);
      } else if (status === 503) {
        settle('closed', BANK_PAGE_UNAVAILABLE);
      } else {
        setMessage(GENERIC_ERROR);
        setPhase('ready');
      }
    }
  };

  const handleDone = async () => {
    if ((phase !== 'opened' && phase !== 'ready') || readOnly) return;
    setLiveViewUrl(null);
    setPhase('checking');
    setMessage(null);
    try {
      await api.post(`${basePath}/done`, { threadId }, await authConfig());
      startPolling();
    } catch (error) {
      const status = statusCode(error);
      if (status === 409) {
        // Someone (or the timeout) already moved it on: just follow along.
        const settled = await fetchStatus(true);
        if (!settled) startPolling();
      } else if (status === 410) {
        settle('expired', challengeOutcomeMessage('expired', null));
      } else if (status === 403) {
        settle('closed', PAYMENT_PRIVATE_ONLY_MESSAGE);
      } else if (status === 404) {
        settle('closed', NO_LONGER_AVAILABLE);
      } else {
        setMessage(GENERIC_ERROR);
        setPhase('ready');
      }
    }
  };

  const handleClose = useCallback(() => {
    stopPolling();
    setLiveViewUrl(null);
    onClose();
  }, [onClose, stopPolling]);

  const remaining = formatRemaining(expiresAt, now);
  const showTimer =
    (phase === 'ready' || phase === 'opening' || phase === 'opened') &&
    remaining !== '';

  const footer = (() => {
    if (phase === 'ready' || phase === 'opening') {
      return (
        <>
          {notice ? (
            <button
              type='button'
              className='tx-btn-primary'
              onClick={handleDone}
              disabled={readOnly}
            >
              Done
            </button>
          ) : (
            <button
              type='button'
              className='tx-btn-primary'
              onClick={handleOpen}
              disabled={readOnly || phase === 'opening'}
            >
              {phase === 'opening' ? 'Opening…' : 'Open bank verification'}
            </button>
          )}
          <button
            type='button'
            className='tx-btn-text'
            onClick={handleClose}
            disabled={phase === 'opening'}
          >
            Not now
          </button>
        </>
      );
    }
    if (phase === 'opened') {
      return (
        <button
          type='button'
          className='tx-btn-primary'
          onClick={handleDone}
          disabled={readOnly}
        >
          Done
        </button>
      );
    }
    if (phase === 'paid' || phase === 'expired' || phase === 'closed') {
      return (
        <button type='button' className='tx-btn-primary' onClick={handleClose}>
          Close
        </button>
      );
    }
    return undefined;
  })();

  return (
    <CredentialSheet
      show={show}
      onClose={handleClose}
      className={`secure-input-sheet payment-challenge-modal${
        phase === 'opened' ? ' live' : ''
      }`}
      ariaLabelledBy='payment-challenge-title'
      holdOpen={phase === 'opening' || phase === 'opened'}
      leading={phase === 'opened' ? undefined : <LockIcon />}
      footer={footer}
    >
      {phase === 'loading' && (
        <p className='brief'>Loading bank verification…</p>
      )}
      {(phase === 'ready' || phase === 'opening') && (
        <div className='tx-head'>
          <h2 className='tx-title hero' id='payment-challenge-title'>
            Confirm the payment <span className='accent'>with your bank</span>
          </h2>
          {origin && (
            <div className='about'>
              <div className='site'>
                <SiteGlyph site={origin} />
                <span>{displayHost(origin)}</span>
              </div>
              <span>Paying at {origin}</span>
            </div>
          )}
          <p className='brief'>
            Your bank wants you to confirm this payment. Open a private,
            time-limited view of the checkout and finish the bank&apos;s step
            there — Dream never sees what you enter. Press Done when the bank
            has confirmed.
          </p>
          {notice && (
            <p className='notice' role='status'>
              {notice}
            </p>
          )}
          {message && (
            <p className='notice danger' role='alert'>
              {message}
            </p>
          )}
          {showTimer && (
            <p className='fine' role='timer'>
              Time left: {remaining}
            </p>
          )}
        </div>
      )}
      {phase === 'opened' && (
        <>
          <div className='tx-head'>
            <h2 className='tx-title hero' id='payment-challenge-title'>
              Finish <span className='accent'>with your bank</span>
            </h2>
            <p className='brief'>
              This is the live checkout. Complete your bank&apos;s step below,
              then press Done.
            </p>
            {showTimer && (
              <p className='fine' role='timer'>
                Time left: {remaining}
              </p>
            )}
          </div>
          {liveViewUrl && (
            <div className='live-view'>
              <iframe
                title='Bank verification'
                src={liveViewUrl}
                sandbox='allow-same-origin allow-scripts allow-forms allow-modals'
                allow='clipboard-read; clipboard-write'
                referrerPolicy='no-referrer'
                data-sensitive='true'
                data-sentry-mask='true'
                className='ph-no-capture'
              />
            </div>
          )}
        </>
      )}
      {phase === 'checking' && (
        <SecureInputStage
          key='checking'
          kind='signing-in'
          title='Checking with the bank…'
          titleId='payment-challenge-title'
          busy
        >
          <p>
            Dream is confirming how the payment went. This can take a moment.
          </p>
        </SecureInputStage>
      )}
      {phase === 'paid' && (
        <SecureInputStage
          key='paid'
          kind='signed-in'
          title='Payment confirmed'
          titleId='payment-challenge-title'
        >
          <p>{message}</p>
        </SecureInputStage>
      )}
      {phase === 'expired' && (
        <SecureInputStage
          key='expired'
          kind='expired'
          title='This bank verification expired'
          titleId='payment-challenge-title'
        >
          <p>{message}</p>
        </SecureInputStage>
      )}
      {phase === 'closed' && (
        <SecureInputStage
          key='closed'
          kind='error'
          title='Unable to continue'
          titleId='payment-challenge-title'
        >
          <p>{message || GENERIC_ERROR}</p>
        </SecureInputStage>
      )}
    </CredentialSheet>
  );
};

export default PaymentChallengeModal;
